import { Box, Drawer, Modal } from '@mantine/core';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import { ReactNode, useCallback, useState } from 'react';

export type OverlayPresentation = 'drawer' | 'modal';

interface FormPresentationShellProps {
  mode: OverlayPresentation;
  title: string;
  busy: boolean;
  dirty: boolean;
  onClose: () => void;
  children: ReactNode | ((requestClose: () => void) => ReactNode);
}

const MIN_DRAWER_WIDTH = 480;

/** Reusable presentation shell for forms opened above a listing. */
export default function FormPresentationShell({
  mode,
  title,
  busy,
  dirty,
  onClose,
  children,
}: FormPresentationShellProps) {
  const { isMobile } = useResponsiveDesign();
  const { confirm } = useConfirm();
  const [drawerWidth, setDrawerWidth] = useState<number | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const minDrawerWidth = Math.min(MIN_DRAWER_WIDTH, window.innerWidth / 2);
  /** Limits the panel to the available desktop viewport. */
  const maxWidth = () => Math.max(minDrawerWidth, Math.floor(window.innerWidth * 0.9));
  /** Keeps drag and keyboard changes within the supported width range. */
  const clampWidth = (width: number) => Math.min(maxWidth(), Math.max(minDrawerWidth, width));

  const requestClose = useCallback(() => {
    if (busy) return;
    if (dirty) {
      setConfirmingClose(true);
      confirm({
        title: 'Zrušenie formulára',
        content: 'Naozaj chcete odísť? Všetky neuložené zmeny budú stratené.',
        zIndex: 1100,
        onConfirm: onClose,
        onClose: () => setConfirmingClose(false),
      });
    } else {
      onClose();
    }
  }, [busy, confirm, dirty, onClose]);

  const resizeHandle = !isMobile && (
    <Box
      role="separator"
      aria-label="Zmeniť šírku bočného panela"
      aria-orientation="vertical"
      aria-valuemin={minDrawerWidth}
      aria-valuemax={maxWidth()}
      aria-valuenow={drawerWidth ?? window.innerWidth / 2}
      tabIndex={0}
      pos="absolute"
      top={0}
      bottom={0}
      left={0}
      w={14}
      sx={{
        cursor: 'ew-resize',
        // Keep the full-height track above Mantine's sticky header (z-index 1000).
        zIndex: 1001,
        borderLeft: '3px solid #a5b4cc',
        backgroundColor: '#eef2f8',
        touchAction: 'none',
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 4,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 4,
          height: 48,
          borderRadius: 4,
          backgroundColor: '#5c7cfa',
        },
        '&:hover, &:focus-visible': { backgroundColor: '#dbe4ff', outline: 'none' },
      }}
      onPointerDown={(event) => event.currentTarget.setPointerCapture(event.pointerId)}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          setDrawerWidth(clampWidth(window.innerWidth - event.clientX));
        }
      }}
      onPointerUp={(event) => event.currentTarget.releasePointerCapture(event.pointerId)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          setDrawerWidth((width) =>
            clampWidth((width ?? window.innerWidth / 2) + (event.key === 'ArrowLeft' ? 24 : -24))
          );
        }
      }}
    />
  );
  const content = typeof children === 'function' ? children(requestClose) : children;

  if (mode === 'drawer') {
    return (
      <Drawer.Root
        opened
        onClose={requestClose}
        position="right"
        size={isMobile ? '100%' : drawerWidth ?? '50vw'}
        closeOnClickOutside={!busy}
        closeOnEscape={!busy}
        trapFocus={!confirmingClose}
        styles={{ content: { position: 'relative', overflow: 'hidden' } }}
      >
        <Drawer.Overlay />
        <Drawer.Content>
          <Box h="100%" sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Drawer.Header sx={{ flexShrink: 0 }}>
              <Drawer.Title>{title}</Drawer.Title>
              <Drawer.CloseButton loading={busy} />
            </Drawer.Header>
            <Drawer.Body
              pb={0}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                '& > form': {
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0,
                },
                '& > form > [data-overlay-form-content]': {
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                },
                '& > form > [data-e2e-capture-role="form-footer"]': {
                  position: 'static',
                  flexShrink: 0,
                },
              }}
            >
              {content}
            </Drawer.Body>
          </Box>
          {resizeHandle}
        </Drawer.Content>
      </Drawer.Root>
    );
  }

  return (
    <Modal
      opened
      onClose={requestClose}
      title={title}
      size="min(1000px, calc(100vw - 24px))"
      closeButtonProps={{ loading: busy }}
      closeOnClickOutside={!busy}
      closeOnEscape={!busy}
      trapFocus={!confirmingClose}
    >
      {content}
    </Modal>
  );
}
